import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  BookOpen,
  CheckCircle2,
  Target,
  TrendingUp,
  Users,
  FileCheck,
  BarChart3,
  GraduationCap,
  Lightbulb,
  Building2,
  ChevronRight,
  Star,
  Monitor,
  Cpu,
  Video,
  Zap
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const DigitalTeachingTools = () => {
  const navigate = useNavigate();

  const pageData = {
    title: 'Digital Teaching Tools & EdTech Training',
    subtitle: 'Master AI-Powered LMS, E-Content Creation & Modern EdTech Tools 2024',
    description: 'Comprehensive training on Learning Management Systems, AI in education, e-content creation, interactive tools, assessment platforms, and digital pedagogy for modern educators',
    meta_title: 'Digital Teaching Tools Training 2024 | AI in Education | LMS | EdTech | ISDE Academy',
    meta_description: 'Professional EdTech training covering AI-powered LMS platforms, MagicSchool AI, e-content creation, digital assessment tools. Save 5+ hours weekly. EdTech market growing at 13.3% CAGR to $348B by 2030.',
    meta_keywords: 'digital teaching tools, EdTech training, AI in education, LMS platforms, e-content creation, MagicSchool AI, online teaching, educational technology, digital pedagogy',
    canonicalUrl: 'https://isde.academy/training/digital-teaching-tools'
  };

  const trainingModules = [
    {
      title: 'AI-Powered Learning Management Systems',
      icon: Cpu,
      duration: '5 Hours',
      topics: [
        'Understanding AI-Driven LMS: $65B Industry by 2030',
        'TalentLMS with TalentCraft: AI Course Creation',
        'Litmos: All-in-One AI-Powered LMS',
        'Docebo: Leader in AI Learning Platforms',
        'Canvas, Moodle, and Blackboard LMS Overview',
        'Natural Language Processing (NLP) in Education',
        'Personalized Learning Paths with AI',
        'Learning Analytics and Data-Driven Insights',
        'Student Engagement Tracking and Intervention'
      ],
      outcomes: [
        'Mastery of AI-powered LMS platforms',
        'Ability to create personalized learning experiences',
        'Data-driven decision making for student success'
      ]
    },
    {
      title: 'AI Tools for Educators (MagicSchool AI & More)',
      icon: Zap,
      duration: '4 Hours',
      topics: [
        'MagicSchool AI: 60+ Tools for 5M Teachers Worldwide',
        'ChatGPT for Lesson Planning and Content Generation',
        'Khanmigo: AI-Powered Tutor and Teaching Assistant',
        'Education Copilot: Streamlining Administrative Tasks',
        'Grammarly for Educational Writing and Feedback',
        'PDF AI for Document Analysis and Summarization',
        'Time-Saving Benefits: 73% Save 5+ Hours Weekly',
        'Ethical Use of AI in Education',
        'Avoiding AI Overreliance and Maintaining Authenticity'
      ],
      outcomes: [
        'Proficiency in leading AI EdTech tools',
        'Time management optimization (5+ hours/week savings)',
        'Ethical AI integration in teaching practice'
      ]
    },
    {
      title: 'E-Content Creation & Multimedia Development',
      icon: Video,
      duration: '6 Hours',
      topics: [
        'Interactive Content Creation Tools',
        'Video Lecture Production: Camtasia, OBS Studio',
        'Animation and Explainer Videos: Powtoon, Vyond',
        'Presentation Enhancement: Prezi, Canva for Education',
        'Infographic Design for Visual Learning',
        'Podcast Creation for Educational Content',
        'Accessible Content: WCAG Guidelines and Universal Design',
        'Copyright, Fair Use, and Creative Commons Licensing',
        'Content Quality Standards and Best Practices'
      ],
      outcomes: [
        'Expertise in multimedia content creation',
        'Ability to produce engaging video lectures',
        'Understanding of accessibility and copyright'
      ]
    },
    {
      title: 'Interactive Learning & Assessment Platforms',
      icon: Monitor,
      duration: '5 Hours',
      topics: [
        'Kahoot!: Gamification and Interactive Quizzes',
        'Mentimeter: Live Polling and Audience Engagement',
        'Padlet: Collaborative Digital Boards',
        'Nearpod: Interactive Lessons and Virtual Reality',
        'Google Classroom: Workflow and Assignment Management',
        'Microsoft Teams for Education: Collaboration Hub',
        'H5P: Interactive HTML5 Content',
        'Formative Assessment Tools: Socrative, Quizizz',
        'Auto-Grading and Feedback Automation'
      ],
      outcomes: [
        'Mastery of interactive teaching platforms',
        'Enhanced student engagement strategies',
        'Efficient assessment and grading workflows'
      ]
    },
    {
      title: 'Virtual Classrooms & Hybrid Teaching',
      icon: Video,
      duration: '5 Hours',
      topics: [
        'Zoom for Education: Features and Best Practices',
        'Microsoft Teams: Virtual Classroom Management',
        'Google Meet: Integration with Google Workspace',
        'Hybrid Teaching Strategies and Best Practices',
        'Engaging Remote Learners Effectively',
        'Breakout Rooms and Collaborative Activities',
        'Recording, Editing, and Sharing Class Sessions',
        'Virtual Whiteboarding: Miro, Jamboard',
        'Managing Technical Challenges and Troubleshooting'
      ],
      outcomes: [
        'Confidence in virtual classroom facilitation',
        'Hybrid teaching methodology expertise',
        'Technical troubleshooting capabilities'
      ]
    },
    {
      title: 'Digital Pedagogy & EdTech Integration',
      icon: Lightbulb,
      duration: '4 Hours',
      topics: [
        'Pedagogical Models for Digital Learning',
        'TPACK Framework: Technology, Pedagogy, Content Knowledge',
        'Flipped Classroom and Blended Learning',
        'Project-Based Learning with Technology',
        'Digital Citizenship and Online Safety',
        'Addressing Digital Divide and Equity Issues',
        'Professional Learning Communities (PLCs) Online',
        'Continuous Professional Development in EdTech',
        'Measuring EdTech Impact: ROI and Learning Outcomes'
      ],
      outcomes: [
        'Strong digital pedagogy foundation',
        'Strategic EdTech integration planning',
        'Ability to measure and demonstrate impact'
      ]
    }
  ];

  const whyChoose = [
    {
      title: 'Latest EdTech 2024',
      description: '$348B market by 2030, growing at 13.3% CAGR - stay ahead of trends',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      title: 'Proven Time Savings',
      description: '73% of teachers using AI tools save 5+ hours weekly',
      icon: Zap,
      color: 'blue'
    },
    {
      title: 'Practical Training',
      description: 'Hands-on experience with 60+ AI tools and leading LMS platforms',
      icon: Lightbulb,
      color: 'purple'
    },
    {
      title: 'Expert Instructors',
      description: 'Trainers with extensive EdTech implementation experience',
      icon: Star,
      color: 'orange'
    }
  ];

  const keyBenefits = [
    {
      title: 'For Educators & Faculty',
      icon: GraduationCap,
      benefits: [
        'Time savings of 5+ hours weekly with AI tools',
        'Enhanced student engagement and participation',
        'Proficiency in 60+ digital teaching tools',
        'Improved content quality and delivery',
        'Effective hybrid and online teaching skills',
        'Professional development and career advancement',
        'Join 79% of teachers using EdTech daily'
      ]
    },
    {
      title: 'For Educational Institutions',
      icon: Building2,
      benefits: [
        'Modernized teaching and learning infrastructure',
        'Improved student learning outcomes and satisfaction',
        'Enhanced institutional competitiveness',
        'Faculty capability building and retention',
        'Better learning analytics and data insights',
        'Cost-effective digital transformation',
        'Alignment with global EdTech trends'
      ]
    }
  ];

  const deliveryOptions = [
    {
      mode: 'On-Campus Workshop',
      duration: '3-4 Days Intensive',
      description: 'Comprehensive on-site training with hands-on practice in AI tools, LMS platforms, content creation software, and live teaching simulations',
      ideal: 'Best for institutions implementing digital transformation initiatives'
    },
    {
      mode: 'Online Live Training',
      duration: '10-12 Sessions (4 weeks)',
      description: 'Interactive online sessions with recorded access, tool tutorials, practice assignments, personalized feedback, and EdTech resource library',
      ideal: 'Flexible option for individual educators and distributed faculty teams'
    },
    {
      mode: 'Hybrid Faculty Development',
      duration: 'Customized (2-4 months)',
      description: 'Blended training combining online modules, on-campus workshops, one-on-one mentoring, and ongoing implementation support with EdTech coaching',
      ideal: 'Complete solution for institutional EdTech adoption and culture change'
    }
  ];

  const targetAudience = [
    'School and College Teachers',
    'University Professors and Lecturers',
    'Educational Technology Coordinators',
    'Instructional Designers',
    'Academic Administrators and Deans',
    'Training and Development Professionals',
    'Early Adopters of EdTech Innovations'
  ];

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <meta name="keywords" content={pageData.meta_keywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />
        <meta property="og:title" content={pageData.meta_title} />
        <meta property="og:description" content={pageData.meta_description} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta name="twitter:title" content={pageData.meta_title} />
        <meta name="twitter:description" content={pageData.meta_description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 text-white py-24 mt-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <Monitor className="h-32 w-32" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Zap className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">EdTech 2024 - AI Era</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">{pageData.title}</h1>
              <p className="text-xl md:text-2xl text-orange-100 mb-4">{pageData.subtitle}</p>
              <p className="text-lg text-orange-50 mb-8 max-w-3xl mx-auto">{pageData.description}</p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg font-semibold"
                >
                  Request Training
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-6 text-lg font-semibold"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Training Modules */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Comprehensive Training Modules</h2>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Structured curriculum covering all aspects of digital teaching from AI-powered tools to e-content creation
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {trainingModules.map((module, index) => {
              const IconComponent = module.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="bg-orange-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-1">{module.title}</h3>
                          <p className="text-sm text-orange-600 font-semibold">Duration: {module.duration}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Topics:</h4>
                        <ul className="space-y-1.5">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle2 className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-orange-50 p-3 rounded-lg">
                        <h4 className="text-xs font-semibold text-orange-700 uppercase mb-1">Learning Outcomes:</h4>
                        <ul className="space-y-1">
                          {module.outcomes.map((outcome, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                              <span className="text-orange-600">•</span>
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Choose Our EdTech Training?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChoose.map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  emerald: 'bg-emerald-50 text-emerald-600',
                  blue: 'bg-blue-50 text-blue-600',
                  purple: 'bg-purple-50 text-purple-600',
                  orange: 'bg-orange-50 text-orange-600'
                };

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full text-center hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <div className={`${colors[item.color]} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {keyBenefits.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-orange-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-orange-600" />
                        </div>
                        <h3 className="text-xl font-bold">{item.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {item.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <ChevronRight className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Delivery Options */}
        <section className="bg-gradient-to-br from-gray-50 to-orange-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Training Delivery Options</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Choose the format that best suits your digital transformation goals
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {deliveryOptions.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-orange-600">{option.mode}</h3>
                      <p className="text-sm font-semibold text-gray-700 mb-3">{option.duration}</p>
                      <p className="text-gray-600 mb-4">{option.description}</p>
                      <div className="bg-orange-50 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-orange-700 uppercase mb-1">Ideal For:</p>
                        <p className="text-sm text-gray-700">{option.ideal}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Target Audience */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Who Should Attend?</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            This training is essential for all educators embracing digital transformation and EdTech innovation
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-orange-100 hover:shadow-md transition-all"
              >
                <Users className="h-5 w-5 text-orange-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{audience}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Transform Your Teaching with EdTech?
              </h2>
              <p className="text-xl text-orange-50 mb-8">
                Join 5 million teachers worldwide saving 5+ hours weekly with AI-powered EdTech tools. Be part of the $348B EdTech revolution!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg font-semibold"
                >
                  Schedule Training
                </Button>
                <Button
                  onClick={() => navigate('/training/hei-training')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-6 text-lg font-semibold"
                >
                  View All HEI Programs
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DigitalTeachingTools;
